// 08 — One bird. Her portrait, her stage, what she has shown you of herself, and
// the three things a keeper can do about it: inspect, handle, treat
// (REFOCUS §12c).

import { t } from '../../i18n';
import { handleBird, inspectBird, treatBird } from '../actions';
import { playHandled } from '../sound';
import { bar } from '../components/bar';
import { chip } from '../components/chip';
import { button, div, h, link, paint } from '../dom';
import { selectBird, selectBirdView } from '../selectors';
import type { BirdView } from '../selectors';
import type { Screen, ScreenContext } from '../router';
import { warningScreen } from './warning';

export function birdScreen(): Screen<HTMLElement> {
  return {
    id: 'bird',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const { store, shell } = ctx.services;
      const chickenId = ctx.params.chicken ?? '';
      const state = store.getState();
      const bird = selectBird(state, chickenId);
      if (!bird) {
        // A chip for a bird that has died since the flock was drawn.
        return missingBird(ctx);
      }
      const view = selectBirdView(state, bird);

      const portrait = div(
        { class: 'cf-portrait' },
        h('span', { class: 'cf-portrait__shadow', attrs: { 'aria-hidden': 'true' } }),
        h('img', {
          class: 'cf-portrait__img cf-clay-loop',
          attrs: { src: view.sprite, alt: t('bird.portraitAlt', { name: view.name }) },
        }),
        chip({ text: t(view.statusKey), tone: view.statusTone, compact: true }),
        h('span', { class: 'cf-portrait__tag', text: t('bird.clayLoop') }),
      );

      // Plain hosts: paint() builds the whole block, layout classes included, so
      // a grid host cannot end up holding one grid child in its first column.
      const stageHost = div({});
      const chipHost = div({});
      const statHost = div({});
      const actionHost = div({});
      const resultHost = div({});

      const screen = div(
        { class: 'cf-screen' },
        div(
          { class: 'cf-screen__head' },
          link({
            class: 'cf-btn cf-btn--link',
            text: t('coop.backToCoop'),
            on: { click: () => ctx.router.back() },
          }),
        ),
        div(
          { class: 'cf-screen__title' },
          div(
            { class: 'cf-stack cf-stack--tight' },
            h('h1', { class: 'cf-display', text: view.name }),
            h('span', {
              class: 'cf-sub',
              text: t('bird.breedLine', { breed: view.breed, sex: view.sexLine }),
            }),
          ),
          chip({ text: t(view.role), tone: 'good' }),
        ),
        portrait,
        stageHost,
        chipHost,
        statHost,
        actionHost,
        resultHost,
      );

      let inspected: ReturnType<typeof inspectBird> | null = null;
      let handled = false;
      let treated = false;

      const paintAll = (): void => {
        paint(
          stageHost,
          view.stages.map((stage) => `${stage.key}:${String(stage.active)}`).join('|'),
          () =>
            div(
              { class: 'cf-row-grid cf-grid-3' },
              ...view.stages.map((stage) =>
                div(
                  {
                    class: `cf-stage${stage.active ? ' cf-stage--on' : ''}`,
                  },
                  h('span', { class: 'cf-stage__name', text: stage.label }),
                  h('span', { class: 'cf-stage__detail', text: stage.detail }),
                ),
              ),
            ),
        );

        paint(chipHost, view.chips.map((entry) => `${entry.key}:${entry.text}`).join('|'), () =>
          div(
            { class: 'cf-chiprow' },
            ...view.chips.map((entry) => chip({ text: entry.text, tone: entry.tone })),
          ),
        );

        paint(
          statHost,
          view.stats.map((stat) => `${stat.key}:${stat.learned}:${stat.stateKey}`).join('|'),
          () =>
            div(
              { class: 'cf-card cf-stack' },
              ...view.stats.map((stat) =>
                bar({
                  label: stat.label,
                  value: stat.learned,
                  valueLabel: t(stat.stateKey, stat.stateParams),
                  tone: 'clay',
                  pending: stat.pending,
                }),
              ),
            ),
        );

        paint(
          actionHost,
          `${inspected ? 'inspected' : ''}|${handled}|${treated}|${view.treatEnabled}`,
          () =>
            div(
              { class: 'cf-row-grid cf-grid-3' },
              button(
                {
                  class: 'cf-btn cf-btn--primary',
                  text: t('bird.inspect'),
                  on: {
                    click: () => {
                      inspected = inspectBird(store, chickenId);
                      paintAll();
                    },
                  },
                },
              ),
              button({
                class: 'cf-btn cf-btn--good',
                text: handled ? t('bird.handledToday') : t('bird.handle'),
                attrs: { disabled: handled },
                on: {
                  click: () => {
                    handleBird(store, chickenId);
                    handled = true;
                    playHandled(store);
                    shell.toast(t('bird.handledToast'));
                    paintAll();
                  },
                },
              }),
              button({
                class: 'cf-btn cf-btn--outline',
                text: view.treatKey === 'bird.treat' ? t('bird.treat') : t(view.treatKey),
                attrs: { disabled: !view.treatEnabled || treated },
                on: {
                  click: () => {
                    if (treatBird(store, chickenId)) {
                      treated = true;
                      shell.toast(t('bird.treatedToast'));
                      paintAll();
                    }
                  },
                },
              }),
            ),
        );

        paint(
          resultHost,
          inspected ? inspected.findings.map((entry) => entry.labelKey).join('|') : handled ? 'handled' : 'none',
          () => {
            if (inspected) {
              return div(
                { class: 'cf-card cf-stack cf-stack--tight' },
                ...inspected.findings.map((entry) =>
                  div(
                    { class: 'cf-findings__row' },
                    chip({ text: t(entry.labelKey), tone: entry.tone }),
                  ),
                ),
              );
            }
            if (handled) {
              return div({ class: 'cf-card' }, h('p', { class: 'cf-small', text: t('bird.handledNote') }));
            }
            return h('p', { class: 'cf-small cf-muted', text: t('bird.inspectNote') });
          },
        );
      };

      // A warning is the same bird seen through her worst vital, so the screen
      // is one push away from where the tap happened.
      const warn = button({
        class: 'cf-btn cf-btn--link',
        text: t('bird.seeWarning'),
        on: { click: () => ctx.router.push(warningScreen(), { chicken: chickenId }) },
      });
      resultHost.after(warn);

      paintAll();
      screen.tabIndex = -1;
      return screen;
    },
  };
}

function missingBird(ctx: ScreenContext<HTMLElement>): HTMLElement {
  const screen = div(
    { class: 'cf-screen' },
    h('h1', { class: 'cf-display', text: t('bird.gone') }),
    h('p', { class: 'cf-sub', text: t('bird.goneNote') }),
    div(
      { class: 'cf-screen__foot' },
      button({
        class: 'cf-btn cf-btn--primary',
        text: t('common.back'),
        on: { click: () => ctx.router.back() },
      }),
    ),
  );
  screen.tabIndex = -1;
  return screen;
}

export type { BirdView };
